import {
    BodyEmailTemplate,
    BrokerDetails,
    ClientApplication,
    ClientLoadModel,
    ClientMyBroker,
    ClientUser,
    EmailReplyPreference,
    EmailTemplateType,
    NumeoRestError,
    SearchBrokerRateConfResult,
} from '@numeo/types-core'
import axios, { isAxiosError } from 'axios'
import dayjs from 'dayjs'

export const sendInfoEmail = async (
    userData: ClientUser,
    recipientEmail: string,
    load: ClientLoadModel,
    mockEmail?: string,
    broker?: ClientMyBroker,
    companyInfo?: {
        companyName?: string
        mcNumber?: string
        phoneNumber?: string
    },
    template?: BodyEmailTemplate,
    footerTemplate?: string,
    emailReplyPreference?: EmailReplyPreference
): Promise<{ emailThreadId: string }> => {
    try {
        // Get template body if available
        let emailTemplate = ''
        if (template) {
            if (template.body) {
                emailTemplate = template.body
            }
        }

        // Use enhanced createInfoMessage with footer support
        const message = createInfoMessage(load, emailTemplate, companyInfo, broker)

        // Get the subject template if available, otherwise use the default generated subject
        const subjectTemplate = userData?.emailTemplates?.bodyTemplates?.find((t) => t.type === EmailTemplateType.INFO)?.subject
        const subject = subjectTemplate ? generateSubject(load, subjectTemplate) : generateSubject(load)

        const response = await axios.post(`/trucks/send-email/${load._id}`, {
            to: { email: mockEmail || recipientEmail },
            subject: subject,
            htmlBody: message + footerTemplate,
            emailType: 'info', // Explicitly mark this as an info request
            emailReplyPreference,
        })

        return { emailThreadId: response.data.data.emailThreadId }
    } catch (error) {
        if (isAxiosError(error)) {
            throw new NumeoRestError(error.response?.data.message || 'Unknown error', error.response?.data.code || 500)
        } else {
            throw new NumeoRestError('Unknown error', 500)
        }
    }
}

export const calculateRatePerMile = (miles: number, rate: number): string => {
    const ratePerMile = rate / miles
    if (isNaN(ratePerMile) || ratePerMile === Infinity) {
        return ''
    }

    return `$${ratePerMile.toFixed(2)}*/mi`
}

export function formatPhoneNumber(input: string): string {
    if (!input) return ''

    const extensionMatch = input.match(/ext\. (\d+)$/)
    const extension = extensionMatch ? extensionMatch[1] : undefined
    const phone = extensionMatch ? input.slice(0, extensionMatch.index).trim() : input.trim()

    const digits = phone.replace(/\D/g, '')

    let formattedNumber = ''
    if (digits.length === 11) {
        formattedNumber = `(${digits.substring(1, 4)}) ${digits.substring(4, 7)}-${digits.substring(7)}`
    } else if (digits.length === 10) {
        formattedNumber = `(${digits.substring(0, 3)}) ${digits.substring(3, 6)}-${digits.substring(6)}`
    } else {
        // Return the original input if it doesn't match standard formats
        formattedNumber = input
    }

    if (extension && formattedNumber !== input) {
        formattedNumber += ` ext. ${extension}`
    }

    return formattedNumber
}

// Email template helpers
const getBrokerName = (broker?: ClientMyBroker) => {
    if (broker) {
        return broker.fullName || broker.companyName || 'team'
    }
    return 'team'
}

export const generateSubject = (load: ClientLoadModel, template?: string): string => {
    // If no template provided, use the default format
    if (!template) {
        return `Load - ${load.origin!.city}, ${load.origin!.stateProv} -> ${load.destination.city}, ${load.destination.stateProv} (${dayjs(load.earliestAvailability).format('MM/DD/YYYY')})${load.originDeadheadMiles?.miles !== 0 ? `| DH-O ${load.originDeadheadMiles.miles} miles` : ''}`
    }

    // Replace template variables with actual values
    return template
        .replace(/{{origin_city}}/g, load.origin?.city || '')
        .replace(/{{origin_state}}/g, load.origin?.stateProv || '')
        .replace(/{{destination_city}}/g, load.destination?.city || '')
        .replace(/{{destination_state}}/g, load.destination?.stateProv || '')
        .replace(/{{total_rate}}/g, load.rateInfo?.rateUsd ? `$${load.rateInfo.rateUsd.toLocaleString()}` : '')
        .replace(/{{date}}/g, dayjs(load.earliestAvailability).format('MM/DD/YYYY'))
        .replace(/{{load_id}}/g, load.postersReferenceId || 'No Reference ID')
        .replace(/{{total_mileage}}/g, load.tripLength?.toString() || '')
        .replace(/{{deadhead_mileage}}/g, load.originDeadheadMiles?.miles?.toString() || '')
}

export const createBidMessage = (
    load: ClientLoadModel,
    totalRate: number = 0,
    template?: string,
    companyInfo?: {
        companyName?: string
        mcNumber?: string
        phoneNumber?: string
    },
    broker?: ClientMyBroker
) => {
    if (!load) return ''

    const brokerName = getBrokerName(broker)
    let messageContent = ''

    // Process main message content
    if (template) {
        // First replace all non-rate template variables
        messageContent = template
            .replace(/{{origin_city}}/g, load.origin!.city)
            .replace(/{{origin_state}}/g, load.origin!.stateProv)
            .replace(/{{destination_city}}/g, load.destination.city)
            .replace(/{{destination_state}}/g, load.destination.stateProv)
            .replace(/{{date}}/g, dayjs(load.earliestAvailability).format('MM/DD/YYYY'))
            .replace(/{{load_id}}/g, load.postersReferenceId || 'N/A')
            .replace(/{{broker_contact_name}}/g, brokerName || 'team')
            .replace(/{{total_mileage}}/g, load.tripLength?.toString() || '')
            .replace(/{{total_rate}}/g, load.rateInfo?.rateUsd?.toString() || '')
            .replace(/{{deadhead_mileage}}/g, load.originDeadheadMiles?.miles?.toString() || '')

        // Format the rate once
        const formattedRate = `$${totalRate.toLocaleString()}`

        // Then handle rate replacements separately to avoid nested replacements
        // Replace each rate-related placeholder ONCE (not using global flag)
        if (messageContent.includes('{{total_rate}}')) {
            messageContent = messageContent.replace('{{total_rate}}', formattedRate)
        }

        if (messageContent.includes('{{rate}}')) {
            messageContent = messageContent.replace('{{rate}}', formattedRate)
        }
    } else {
        messageContent = `<p>Hello Team,</p>

<p>I'm reaching out regarding your order from ${load.origin!.city}, ${load.origin!.stateProv}, to ${load.destination.city}, ${load.destination.stateProv}. I'd like to propose a rate of <b>$${totalRate.toLocaleString()}</b>.</p>

<p>Could you let us know if this proposal works for you or if you'd like to discuss further?</p>`
    }

    // Default footer if no template is provided
    return messageContent
}

const createInfoMessage = (
    load: ClientLoadModel,
    template?: string,
    companyInfo?: {
        companyName?: string
        mcNumber?: string
        phoneNumber?: string
    },
    broker?: ClientMyBroker
) => {
    if (!load) return ''

    if (template) {
        const brokerName = getBrokerName(broker)

        // Process main message content
        const messageContent = template
            .replace(/{{origin_city}}/g, load.origin!.city)
            .replace(/{{origin_state}}/g, load.origin!.stateProv)
            .replace(/{{destination_city}}/g, load.destination.city)
            .replace(/{{destination_state}}/g, load.destination.stateProv)
            .replace(/{{date}}/g, dayjs(load.earliestAvailability).format('MM/DD/YYYY'))
            .replace(/{{load_id}}/g, load.postersReferenceId || 'N/A')
            .replace(/{{broker_contact_name}}/g, brokerName || 'team')
            .replace(/{{total_mileage}}/g, load.tripLength?.toString() || '0')
            .replace(/{{total_rate}}/g, load.rateInfo?.rateUsd?.toString() || '0')
            .replace(/{{deadhead_mileage}}/g, load.originDeadheadMiles?.miles?.toString() || '0')

        return messageContent
    } else {
        const messageContent = `<p>Hello Team!</p>
<p>Need details on the <strong>${load.origin.city}, ${load.origin.stateProv}</strong> to <strong>${load.destination.city}, ${load.destination.stateProv}</strong>, <strong>${dayjs(load.earliestAvailability).format('MM/DD/YYYY')}</strong></p>
`

        return messageContent
    }
}

export const createBrokerMessage = (broker: BrokerDetails, rateCon: SearchBrokerRateConfResult, application?: ClientApplication, template?: string) => {
    if (template) {
        const route = rateCon.route ?? []
        const originStop = route[0]
        const destinationStop = route[route.length - 1]
        return template
            .replace(/{{origin_city}}/g, originStop?.location?.city || 'N/A')
            .replace(/{{origin_state}}/g, originStop?.location?.state || 'N/A')
            .replace(/{{destination_city}}/g, destinationStop?.location?.city || 'N/A')
            .replace(/{{destination_state}}/g, destinationStop?.location?.state || 'N/A')
            .replace(/{{broker_contact_name}}/g, broker.contact.name || broker.companyName || 'team')
            .replace(/{{project_name}}/g, application?.projectName || 'team')
    }

    return `<p>Hello Team,</p>

<p>This is ${application?.projectName || 'our team'} reaching out. I noticed that we previously worked together on a load out of <strong>${broker.address.city}, ${broker.address.state}</strong>, and you had sent us a rate confirmation for that origin.</p>

<p>I'm currently looking for another load out of <strong>${broker.address.city}, ${broker.address.state}</strong>. Do you happen to have anything available from that area at the moment?</p>

<p>Please let me know if there's anything you can offer. Looking forward to working with you again!</p>`
}

/**
 * Calculate distance between two coordinates using the Haversine formula
 * @param lat1 Latitude of first point in degrees
 * @param lon1 Longitude of first point in degrees
 * @param lat2 Latitude of second point in degrees
 * @param lon2 Longitude of second point in degrees
 * @returns Distance in miles
 */
export const calculateDistance = (lat1: number, lon1: number, lat2: number, lon2: number): number => {
    // Convert latitude and longitude from degrees to radians
    const toRadians = (degrees: number) => (degrees * Math.PI) / 180

    const dLat = toRadians(lat2 - lat1)
    const dLon = toRadians(lon2 - lon1)

    // Haversine formula
    const a = Math.sin(dLat / 2) * Math.sin(dLat / 2) + Math.cos(toRadians(lat1)) * Math.cos(toRadians(lat2)) * Math.sin(dLon / 2) * Math.sin(dLon / 2)

    const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a))

    // Earth's radius in miles
    const radius = 3958.8 // miles

    // Calculate the distance
    return radius * c
}

/**
 * Extract email addresses from text using regex
 * @param text Text to search for email addresses
 * @returns First valid email found or null if none exists
 */
export const extractEmailFromText = (text: string): string | null => {
    if (!text) return null

    // Regex pattern to match email addresses
    const emailRegex = /([a-zA-Z0-9._-]+@[a-zA-Z0-9._-]+\.[a-zA-Z0-9_-]+)/gi
    const matches = text.match(emailRegex)

    return matches && matches.length > 0 ? matches[0] : null
}
